/**
 * Utility functions for text search highlighting in TimelineItem component.
 *
 * These are the single source of truth for "what does the search query match on
 * this item, and in what order" — consumed both by the Timeline's match
 * navigator (the "N of M" counter and ↑↓ arrows, see useTimeline) and by the
 * renderer that draws the <mark> elements (TimelineItemContentV2). The two must
 * agree exactly or the counter desyncs from the highlights, so neither should
 * count matches on its own.
 */

import { formatISODate } from "~/common/converters";
import { decodeEditorMarkdownWithOffsets } from "../../TipTapEditor/utils/diffUtils";

/**
 * Escape special regex characters in a string
 * @param s - The string to escape
 * @returns Escaped string safe for use in regex
 */
export const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Any raw HTML tag, e.g. the `<sup …>` citation markers injected into a summary. */
const HTML_TAG = /<[^>]*>/g;

/**
 * Highlight search terms in content with mark tags
 *
 * This function adds highlight markers around search terms in markdown content.
 * It preserves the original markdown structure for later rendering.
 *
 * Matching runs on the text the editor shows (`findMarkdownQueryRanges`), so
 * `snake_case` finds a stored `snake\_case` and `>` a stored `&gt;`; each mark
 * wraps whole escapes and entities, which the renderer then decodes.
 *
 * Tag interiors are skipped. The content may already carry raw HTML — citation
 * markers arrive as `<sup data-citation-ids="…">` — and highlighting inside one
 * would split the tag with a `<mark>`, so rehypeRaw renders the wreckage as
 * literal text instead of an element. Skipping tags also keeps the match set
 * equal to `countSearchMatches` over the un-injected content, which is what
 * `activeMatchIndex` is numbered against.
 *
 * @param content - The content to search within (markdown text)
 * @param searchQuery - The search term to highlight
 * @param activeMatchIndex - Index of the match to mark as active (-1 for none)
 * @returns Content with search terms wrapped in mark tags
 */
export const highlightSearchTerm = (
  content: string,
  searchQuery: string,
  activeMatchIndex: number = -1,
): string => {
  if (!searchQuery || !searchQuery.trim()) {
    return content;
  }

  // Counting spans the whole content, not each segment, so activeMatchIndex
  // stays global.
  let matchCount = 0;
  const highlightSegment = (segment: string): string => {
    let highlighted = "";
    let cursor = 0;
    for (const { start, end } of findMarkdownQueryRanges(
      segment,
      searchQuery,
    )) {
      const isActive = matchCount === activeMatchIndex;
      matchCount++;
      highlighted += segment.slice(cursor, start);
      highlighted += `<mark class="${isActive ? "indigo-timeline-item__mark--active" : ""}">${segment.slice(start, end)}</mark>`;
      cursor = end;
    }
    return highlighted + segment.slice(cursor);
  };

  let highlightedContent = "";
  let cursor = 0;
  for (const tag of content.matchAll(HTML_TAG)) {
    highlightedContent += highlightSegment(content.slice(cursor, tag.index));
    highlightedContent += tag[0]; // passes through untouched
    cursor = tag.index + tag[0].length;
  }
  highlightedContent += highlightSegment(content.slice(cursor));

  return highlightedContent;
};

/**
 * Count the number of search matches in a text string
 * @param text - The text to search within
 * @param searchQuery - The search term to count
 * @returns Number of matches found
 */
export const countSearchMatches = (
  text: string,
  searchQuery: string | undefined,
): number => {
  if (!searchQuery || !searchQuery.trim() || !text) return 0;
  const query = escapeRegExp(searchQuery.trim());
  const regex = new RegExp(query, "gi");
  const matches = text.match(regex);
  return matches ? matches.length : 0;
};

/**
 * A [start, end) character range within a field's text.
 */
export interface MatchRange {
  start: number;
  end: number;
}

/**
 * Literal, case-insensitive occurrences of `searchQuery` in `text`.
 */
export const findQueryRanges = (
  text: string,
  searchQuery: string | undefined,
): MatchRange[] => {
  if (!searchQuery || !searchQuery.trim() || !text) return [];
  const regex = new RegExp(escapeRegExp(searchQuery.trim()), "gi");
  return [...text.matchAll(regex)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
};

/**
 * `findQueryRanges` for stored entry Markdown. The editor (@tiptap/markdown
 * ≥3.22) saves `snake\_case` and `&gt;`, so the query is matched against the
 * decoded text and each hit is mapped back to raw offsets that keep escapes
 * and entities whole. Unescaped (older) text matches exactly as before.
 */
export const findMarkdownQueryRanges = (
  markdown: string,
  searchQuery: string | undefined,
): MatchRange[] => {
  if (!searchQuery || !searchQuery.trim() || !markdown) return [];
  const { text, rawOffsets } = decodeEditorMarkdownWithOffsets(markdown);
  return findQueryRanges(text, searchQuery).map(({ start, end }) => ({
    start: rawOffsets[start],
    end: rawOffsets[end],
  }));
};

/** ISO dates the date matcher understands: "YYYY-MM-DD". */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Normalizes date separators so "/", "." and "-" are interchangeable — lets a
 * query typed as "3/5/2024" match the same item as "3-5-2024". Separators are
 * replaced 1:1, so offsets into the normalized string are valid in the original.
 */
const normalizeDateSeparators = (value: string): string =>
  value.replace(/[/.]/g, "-");

/**
 * Ranges to highlight within an item's DISPLAYED date ("03/05/2024"), for a
 * query that may be written in any of the accepted date notations.
 *
 * A query that literally appears in the displayed date highlights just that
 * part ("03/05" or "2024" underlines a substring). A query written in another
 * notation — the raw ISO value "2024-03-05", or unpadded "3/5/2024" — has no
 * corresponding substring to underline, so the whole displayed date is marked
 * as the single match.
 *
 * @param isoDate - The item's date as stored ("YYYY-MM-DD"), or null
 * @param searchQuery - The raw search query
 * @returns Ranges into `formatISODate(isoDate)`, empty when nothing matches
 */
export const findDateMatchRanges = (
  isoDate: string | null,
  searchQuery: string | undefined,
): MatchRange[] => {
  if (!isoDate || !searchQuery || !searchQuery.trim()) return [];

  const parts = isoDate.match(ISO_DATE);
  // Not a date we can reformat (shouldn't happen — `date` is stored as ISO).
  // Fall back to matching the raw value literally rather than guessing.
  if (!parts) return findQueryRanges(isoDate, searchQuery);

  const [, year, month, day] = parts;
  const display = formatISODate(isoDate);
  const query = normalizeDateSeparators(searchQuery.toLowerCase().trim());

  // Substring of the displayed form — highlight exactly what matched. The
  // separator swap is length-preserving, so these offsets index `display`.
  const displayRanges = findQueryRanges(
    normalizeDateSeparators(display),
    query,
  );
  if (displayRanges.length > 0) return displayRanges;

  // Written in another notation — mark the whole displayed date.
  const alternateForms = [
    `${year}-${month}-${day}`,
    `${Number(month)}-${Number(day)}-${year}`,
  ];
  if (alternateForms.some((form) => form.includes(query))) {
    return [{ start: 0, end: display.length }];
  }

  return [];
};

/**
 * The fields of a timeline item that search matches against.
 */
export interface SearchableItemFields {
  date: string | null;
  title: string;
  doctorName: string;
  /** Stored entry Markdown — may carry editor escapes (`findMarkdownQueryRanges`). */
  content: string;
}

/**
 * The search matches on one item: a count per field, numbered in the order
 * date → title → doctorName → content (visual top-to-bottom), plus the date
 * ranges the date count was derived from. `activeMatchIndex` is an index into
 * that concatenated sequence.
 */
export interface ItemSearchMatches {
  date: number;
  title: number;
  doctorName: number;
  content: number;
  total: number;
  /**
   * Ranges into the item's displayed date, carried alongside the counts so the
   * renderer marks exactly what was counted without recomputing it.
   */
  dateRanges: MatchRange[];
}

/**
 * Finds the search matches on an item, per field.
 *
 * Both the match navigator and the highlight renderer derive their numbering
 * from this, so a match is never counted in one and skipped in the other.
 */
export const getItemSearchMatches = (
  fields: SearchableItemFields,
  searchQuery: string | undefined,
): ItemSearchMatches => {
  const dateRanges = findDateMatchRanges(fields.date, searchQuery);
  const date = dateRanges.length;
  const title = countSearchMatches(fields.title, searchQuery);
  const doctorName = countSearchMatches(fields.doctorName, searchQuery);
  // Same ranges highlightSearchTerm marks, so the counter never desyncs.
  const content = findMarkdownQueryRanges(fields.content, searchQuery).length;
  return {
    date,
    title,
    doctorName,
    content,
    total: date + title + doctorName + content,
    dateRanges,
  };
};
